import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import moment from "moment";
import { Observable } from "rxjs";
import { dateToUtcIsoString } from "src/app/common/datetime_utility";
import { AutocompleteSearchResultsModel } from "src/app/models/api/autocompleteSearchResults.model";
import { FileDownloadModel } from "src/app/models/api/fileDownload.model";
import {
    InventoryOrgWideAccessCategory,
    InventoryResourceGraph,
    InventoryResourceModel,
    ResourceCategory,
} from "src/app/models/api/inventoryResource.model";
import { JsonHalCollection } from "src/app/models/api/jsonhal.model";
import { ISelectedFilters } from "src/app/models/filters/selectedFilters.interface";
import { SortColumn } from "src/app/models/sortColumn.model";
import { LoggerService } from "../../infra/logger/logger.service";
import { HttpService } from "../http/http.service";

@Injectable({
    providedIn: "root",
})
export class InventoryHttpService {
    public readonly defaultPageSize: number = 50;

    constructor(
        private _httpService: HttpService,
        private _logger: LoggerService,
    ) {}

    /**
     * @public Get single inventory resource by id
     * @returns an Observable{InventoryResourceModel} with the resource returned from server
     */
    public getInventoryResource(integrationId: string, resourceId: string): Observable<InventoryResourceModel> {
        const url = `inventory/integrations/${integrationId}/resources/${resourceId}`;
        return this._httpService.get<InventoryResourceModel>(url);
    }

    public getInventoryResourceGraph(integrationId: string, resourceId: string, depth: number): Observable<InventoryResourceGraph> {
        const url = `inventory/integrations/${integrationId}/resources/${resourceId}/graph`;
        const httpParams = new HttpParams().append("depth", depth);
        return this._httpService.get<InventoryResourceGraph>(url, undefined, httpParams);
    }

    /**
     * @public Get single inventory autocomplete options
     * @returns an Observable{AutocompleteSearchResultsModel} with the displayNames
     */
    public getAutocompleteOptions(query: string, filter_name: string): Observable<AutocompleteSearchResultsModel[]> {
        const url = `inventory/autocomplete`;
        const httpParams = this._createInventoryQueryParams({ query: query, filter_name: filter_name }, this.defaultPageSize);
        return this._httpService.get<AutocompleteSearchResultsModel[]>(url, undefined, httpParams);
    }

    /**
     * @public Start getting first page of inventory resources
     * @returns an Observable{InventoryResourceModel} with the resource returned from server
     */
    public startGetInventoryResources(filterOptions: ISelectedFilters, sortData?: SortColumn): Observable<JsonHalCollection<InventoryResourceModel>> {
        const url = this._getBaseUrlBasedOnCategory(filterOptions.category);
        const httpParams = this._createInventoryQueryParams(filterOptions, this.defaultPageSize, sortData);
        return this._httpService.get<JsonHalCollection<InventoryResourceModel>>(url, undefined, httpParams);
    }

    /**
     * @public Gets all Inventory resources by given ids as a single page
     * @returns an Observable{InventoryResourceModel} with the resources returned from server
     */
    public getInventoryResourcesByIds(resourcesIds: string[]): Observable<JsonHalCollection<InventoryResourceModel>> {
        const url = "inventory/resources";
        const pageSize = resourcesIds.length > this.defaultPageSize ? resourcesIds.length : this.defaultPageSize;
        const httpParams = this._createInventoryQueryParams({ resourceId: resourcesIds }, pageSize);
        return this._httpService.get<JsonHalCollection<InventoryResourceModel>>(url, undefined, httpParams);
    }

    /**
     * @public Continue getting inventory resources using a continuation token
     * @returns an Observable{InventoryResourceModel} with the resource returned from server
     */
    public getInventoryResourcesByContinuationToken(continuationToken: string): Observable<JsonHalCollection<InventoryResourceModel>> {
        const url = "inventory/resources-pages";
        const httpParams = new HttpParams().append("continuationToken", continuationToken);
        return this._httpService.get<JsonHalCollection<InventoryResourceModel>>(url, undefined, httpParams);
    }

    /**
     * @public Get display name by id
     * @returns an Observable{integrationModel} with the integrations returned from server
     */
    public getDisplayNameById(filter_name: string, filter_id: string[]): Observable<AutocompleteSearchResultsModel[]> {
        const url = `inventory/autocomplete/options`;
        const httpParams = this._createInventoryQueryParams({ filter_id: filter_id, filter_name: filter_name }, this.defaultPageSize);
        return this._httpService.get<AutocompleteSearchResultsModel[]>(url, undefined, httpParams);
    }

    /**
     * @public Get inventory resources file
     * @returns a file with the inventory resources returned from server
     */
    public exportFilteredResources(filterOptions: ISelectedFilters, sortData?: SortColumn): Observable<FileDownloadModel> {
        const baseUrl = this._getBaseUrlBasedOnCategory(filterOptions.category);
        const httpParams = this._createInventoryQueryParams(filterOptions, this.defaultPageSize, sortData, false);
        return this._httpService.get<FileDownloadModel>(`${baseUrl}/export`, undefined, httpParams);
    }

    private _getBaseUrlBasedOnCategory(categoryTypes?: string[]): string {
        if (categoryTypes && categoryTypes.length == 1) {
            switch (categoryTypes[0]) {
                case ResourceCategory.identity:
                    return "inventory/identities";
                case ResourceCategory.connection:
                    return "inventory/connections";
                case ResourceCategory.automation:
                    return "inventory/automation";
                case ResourceCategory.environment:
                    return "inventory/environment";
                case ResourceCategory.bot:
                    return "inventory/bot";
            }
        }
        return "inventory/resources";
    }

    private _createInventoryQueryParams(
        filterOptions: ISelectedFilters,
        pageSize: number,
        sortData?: SortColumn,
        shouldLimitResultsAmount = true,
    ): HttpParams {
        let httpParams = new HttpParams();
        if (shouldLimitResultsAmount) {
            httpParams = httpParams.append("pageSize", pageSize);
        }

        httpParams = this._appendInventoryFilterQueryParams(httpParams, filterOptions);

        if (sortData) {
            httpParams = this._appendInventorySortQueryParams(httpParams, sortData);
        }
        return httpParams;
    }

    private _appendInventoryFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
        if (filterOptions.resourceId && filterOptions.resourceId.length > 0) {
            httpParams = httpParams.append("id", filterOptions.resourceId.join(","));
        }

        if (filterOptions.integration && filterOptions.integration.length > 0) {
            httpParams = httpParams.append("integrationId", filterOptions.integration.join(","));
        }

        if (filterOptions.category && filterOptions.category.length > 0) {
            httpParams = httpParams.append("resourceCategory", filterOptions.category.join(","));
        }

        if (filterOptions.type && filterOptions.type.length > 0) {
            httpParams = httpParams.append("resourceType", filterOptions.type.join(","));
        }

        if (filterOptions.resourceStatus && filterOptions.resourceStatus.length > 0) {
            httpParams = httpParams.append("resourceStatus", filterOptions.resourceStatus.join(","));
        }

        if (filterOptions.platform && filterOptions.platform.length > 0) {
            httpParams = httpParams.append("platform", filterOptions.platform.join(","));
        }

        if (filterOptions.fromDate) {
            httpParams = httpParams.append("creationTimeSince", dateToUtcIsoString(moment(filterOptions.fromDate).startOf("day").toDate()));
        }

        if (filterOptions.toDate) {
            httpParams = httpParams.append("creationTimeUntil", dateToUtcIsoString(moment(filterOptions.toDate).endOf("day").toDate()));
        }

        if (filterOptions.businessCriticality && filterOptions.businessCriticality.length > 0) {
            httpParams = httpParams.append("businessCriticality", filterOptions.businessCriticality.join(","));
        }

        if (filterOptions.securityRisk && filterOptions.securityRisk.length > 0) {
            httpParams = httpParams.append("securityRisk", filterOptions.securityRisk.join(","));
        }

        if (filterOptions.generativeAiOptions && filterOptions.generativeAiOptions.length > 0) {
            httpParams = httpParams.append("generativeAiOptions", filterOptions.generativeAiOptions.join(","));
        }

        if (filterOptions.query && filterOptions.query.length > 0) {
            httpParams = httpParams.append("query", filterOptions.query);
        }

        if (filterOptions.filter_name && filterOptions.filter_name.length > 0) {
            httpParams = httpParams.append("filterName", filterOptions.filter_name);
        }

        if (filterOptions.filter_id && filterOptions.filter_id.length > 0) {
            httpParams = httpParams.append("filterId", `${filterOptions.filter_id}`);
        }

        if (filterOptions.search) {
            httpParams = httpParams.append("textSearch", `${filterOptions.search}`);
        }

        if (filterOptions.environment && filterOptions.environment.length > 0) {
            httpParams = httpParams.append("environmentId", `${filterOptions.environment}`);
        }

        if (filterOptions.createdBy && filterOptions.createdBy.length > 0) {
            httpParams = httpParams.append("createdById", `${filterOptions.createdBy}`);
        }

        if (filterOptions.ownedBy && filterOptions.ownedBy.length > 0) {
            httpParams = httpParams.append("ownedById", `${filterOptions.ownedBy}`);
        }

        if (filterOptions?.department?.length) {
            httpParams = httpParams.append("createdByDepartment", `${filterOptions.department}`);
        }

        if (filterOptions.lastModifiedSinceDate && filterOptions.lastModifiedSinceDate.length > 0) {
            const since = moment().subtract(30, "days").toISOString();
            httpParams = httpParams.append("lastModifiedSince", since);
        }

        if (filterOptions.identityIsExternal !== undefined) {
            httpParams = httpParams.append("isExternal", filterOptions.identityIsExternal);
        }

        if (filterOptions.allBuilders !== undefined) {
            httpParams = httpParams.append("isBuilder", filterOptions.allBuilders);
        }

        if (filterOptions.connectionConnectorType && filterOptions.connectionConnectorType.length > 0) {
            httpParams = httpParams.append("connectorType", filterOptions.connectionConnectorType.join(","));
        }

        if (filterOptions.connectionInUse !== undefined) {
            httpParams = httpParams.append("inUse", filterOptions.connectionInUse);
        }

        if (filterOptions.relatedResource !== undefined) {
            httpParams = httpParams.append("relatedResourceId", filterOptions.relatedResource);
        }

        if (filterOptions.automationUsesAiModel !== undefined) {
            httpParams = httpParams.append("usesAiModel", filterOptions.automationUsesAiModel);
        }

        if (filterOptions.automationUsesWebhook !== undefined) {
            httpParams = httpParams.append("usesWebhookTrigger", filterOptions.automationUsesWebhook);
        }

        if (filterOptions.automationUsesHttpStepsWithoutAuthentication !== undefined) {
            httpParams = httpParams.append("usesHttpStepsWithoutAuthentication", filterOptions.automationUsesHttpStepsWithoutAuthentication);
        }

        if (filterOptions.automationIsBotTrigger !== undefined) {
            httpParams = httpParams.append("isBotTrigger", filterOptions.automationIsBotTrigger);
        }

        if (filterOptions.environmentType !== undefined) {
            httpParams = httpParams.append("environmentType", filterOptions.environmentType);
        }

        if (filterOptions.botType !== undefined) {
            httpParams = httpParams.append("botType", filterOptions.botType);
        }

        if (filterOptions.orgWideAccess !== undefined) {
            httpParams = httpParams.append(
                "orgWideAccess",
                [InventoryOrgWideAccessCategory.viewer, InventoryOrgWideAccessCategory.builder].join(","),
            );
        }

        if (filterOptions.publicAccess !== undefined) {
            httpParams = httpParams.append("publicAccess", [InventoryOrgWideAccessCategory.viewer, InventoryOrgWideAccessCategory.builder].join(","));
        }

        return httpParams;
    }

    private _appendInventorySortQueryParams(httpParams: HttpParams, sortData: SortColumn): HttpParams {
        const sortFieldString: string | undefined = sortData.column.sortKey;
        const ascOrDescString = sortData.isDesc ? "desc" : "asc";

        if (sortFieldString) {
            httpParams = httpParams.append("sortBy", `[${sortFieldString}(${ascOrDescString})]`);
        } else {
            this._logger.error(`Sort inventory column ${sortData.column.propertyKey} was not implemented`);
        }
        return httpParams;
    }
}
